import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24" aria-labelledby={`${id}-h`}>
    <h2 id={`${id}-h`} className="text-xl text-[#D4AF37] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{title}</h2>
    <div className="text-white/60 text-sm leading-relaxed font-light space-y-3">{children}</div>
  </section>
);

function TermsHardcoded() {
  return (
    <div data-testid="terms-page" className="min-h-screen">
      <SEOHead
        title="Terms of Service"
        description="JS Fragrance Terms of Service — covers orders, payments, returns, accounts, ambassador program, custom scent builder, intellectual property, and dispute resolution."
        url="https://jsfragrances.com/terms"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Terms of Service', path: '/terms' }]} />

      <div className="bg-[#0A0A0A] py-12 sm:py-16 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">Legal</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Terms of Service
          </h1>
          <p className="text-white/40 text-sm mt-3 font-sans">Last updated: February 1, 2026</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-10">
        <Section id="acceptance" title="1. Acceptance of Terms">
          <p>
            Welcome to JS Fragrance. By accessing, browsing, or purchasing from <strong className="text-white/80">jsfragrances.com</strong>,
            you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, please do not use our services.
          </p>
          <p>
            These terms apply to all visitors, registered customers, ambassadors, dealers, and anyone else accessing our platform.
          </p>
        </Section>

        <Section id="who-we-are" title="2. Who We Are">
          <p>
            JS Fragrance is a Philippine luxury fragrance brand based in Manila. We hand-blend every scent as
            <strong className="text-white/80"> Eau de Parfum (EDP) bottled at 30% oil concentration — Extrait-level strength</strong> — and ship nationwide.
            Contact: <a href="mailto:support@jsfragrances.com" className="text-[#D4AF37] hover:underline">support@jsfragrances.com</a>.
          </p>
        </Section>

        <Section id="products-pricing" title="3. Products & Pricing">
          <p>
            All prices are listed in Philippine Peso (PHP) and inclusive of applicable taxes unless stated otherwise.
            Prices, product availability, ingredients, and descriptions may be updated without prior notice.
          </p>
          <p>
            We reserve the right to limit quantities, refuse or cancel orders, and correct pricing or descriptive errors —
            even after an order has been submitted. If we cancel a paid order due to such an error, you will receive a full refund.
          </p>
          <p>
            <strong className="text-white/80">JS Originals</strong> are exclusive in-house creations. <strong className="text-white/80">Inspired-by</strong> fragrances
            are our independent interpretations of well-known scent profiles; we never copy logos, names, packaging, or formulas of other brands.
          </p>
        </Section>

        <Section id="orders-payment" title="4. Orders & Payment">
          <p>Orders are confirmed only after payment verification. We accept:</p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37]">◆</span> GCash, GoTyme, PayMaya/Maya, bank transfer</li>
            <li className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Credit/debit card via secure third-party processors (where enabled)</li>
            <li className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Cash on Delivery (COD) where available — first-time COD orders may be subject to a verification call</li>
          </ul>
          <p>
            For non-COD and non-card orders, you must upload a clear screenshot of your payment transaction at checkout.
            Each payment is <strong className="text-white/80">manually verified</strong> by our team before the order enters the packing queue.
            Verification typically completes within 24 hours, often sooner. Orders without verified payment within 48 hours
            may be cancelled and the inventory released.
          </p>
        </Section>

        <Section id="cancellation" title="5. Order Cancellation & Modification">
          <p>
            You may cancel or modify your order within <strong className="text-white/80">1 hour of placement</strong>, before it enters
            our packing queue. Email or message support with your order number as soon as possible. Once an order is packed and
            handed to the courier, we cannot recall it.
          </p>
        </Section>

        <Section id="shipping" title="6. Shipping & Delivery">
          <p>
            Shipping fees and timelines are detailed in our <a href="/shipping-policy" className="text-[#D4AF37] hover:underline">Shipping Policy</a>.
            Once handed to the courier, transit times are subject to courier performance, weather, and Philippine holidays.
          </p>
        </Section>

        <Section id="returns" title="7. Returns & Authenticity">
          <p>
            Refund and exchange terms are detailed in our <a href="/refund-policy" className="text-[#D4AF37] hover:underline">Refund Policy</a>.
            Every JS Fragrance product is hand-blended and bottled in our Philippine facility — there are no resellers, no counterfeit
            risk in the supply chain. Each product page also displays our 100% Authentic guarantee.
          </p>
        </Section>

        <Section id="accounts" title="8. Customer Accounts">
          <p>
            You are responsible for keeping your account credentials confidential and for all activity under your account.
            We use bcrypt-hashed passwords (we cannot see your raw password). Two-factor authentication (2FA) is enforced
            for admin accounts.
          </p>
          <p>
            You may delete your account at any time by contacting support. Deletion removes your personal profile data;
            order records may be retained per Philippine tax/business-record requirements.
          </p>
        </Section>

        <Section id="custom-scent" title="9. Custom Scent Builder">
          <p>
            Personalised fragrances created via the Custom Scent Builder are made-to-order based on your specific selections.
            Because each blend is unique to you, custom orders are <strong className="text-white/80">non-refundable except in cases of defect or fulfillment error</strong>.
            Production typically takes 5-10 business days before shipping.
          </p>
        </Section>

        <Section id="bundle" title="10. Bundle Builder">
          <p>
            Multi-product bundle discounts are applied automatically at checkout when the qualifying bundle conditions are met
            (e.g., Pick 3 scents and save 15%). Bundle items follow the same return policy as individual products. The bundle
            discount may not be combined with other promotional codes unless explicitly stated.
          </p>
        </Section>

        <Section id="ambassador" title="11. Ambassador Program">
          <p>
            Our Ambassador Program lets independent sellers earn from JS Fragrance through two clear, one-level mechanics.
            <strong className="text-white/80"> There is no pyramiding, no downline royalties, and no chain commissions.</strong> Every
            ambassador earns only from their own activity — never from the activity of someone they recruited&apos;s recruits.
          </p>
          <p>
            <strong className="text-white/80">Joining as an Ambassador.</strong> You apply at <a href="/ambassador-apply" className="text-[#D4AF37] hover:underline">/ambassador-apply</a>.
            Your account is auto-created on submission, and you must purchase the Brand Ambassador Tester Kit to activate your
            dashboard. Until your tester-kit payment is manually approved by JS, you can only see the &quot;Pending Payment&quot; screen —
            no leaderboard, no referral link, no commissions.
          </p>
          <p><strong className="text-white/80">How ambassadors earn money:</strong></p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> <strong>Per-bottle profit margin (your own sales).</strong> When you re-sell a bottle to your own customer, you keep the difference between your selling price and the ambassador wholesale rate. At the current Ambassador tier this is <strong>₱100 profit per 50ml bottle</strong> (sell at ₱355 retail, source at ₱255 wholesale). This is YOUR profit on bottles YOU personally sell — not a commission paid by JS, and not derived from anyone else&apos;s sales.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> <strong>One-time tester-kit referral bonus.</strong> When someone you DIRECTLY referred via your unique referral link gets their tester-kit payment approved, you receive a one-time bonus (currently ₱200) credited to your ambassador wallet. This applies only to your direct referees — never to their referees, and never on the referee&apos;s subsequent product purchases.</li>
          </ul>
          <p>
            <strong className="text-white/80">Tier structure.</strong> The program has a single Ambassador tier plus three volume-based
            Distributor tiers (Distributor 100–299 pcs, Bulk Partner 300–499 pcs, Super Bulk 500+ pcs). Higher tiers unlock deeper wholesale
            margins on bulk orders — they do <em>not</em> create downline commissions. The legacy &quot;Bronze / Silver / Gold&quot; recognition badges
            from earlier versions of this program have been retired.
          </p>
          <p>
            <strong className="text-white/80">Monthly Top Seller</strong> — the ambassador with the most bottles sold each month receives the
            announced reward bundle (free 50ml bottle of your choice + cash bonus + spotlight feature post + official downloadable certificate).
            Only delivered + fully-paid orders count toward the monthly tally; pending payments roll into the next month&apos;s count. Winners
            claim the prize from their certificate page (<a href="/certificate/2026/1" className="text-[#D4AF37] hover:underline">/certificate/&#123;year&#125;/&#123;month&#125;</a>)
            by filling in the &quot;Claim Your Prize&quot; form (GCash number + scent choice). Claims are fulfilled within 1–2 business days after admin review.
          </p>
          <p>
            <strong className="text-white/80">Suspension / termination.</strong> We may suspend or terminate ambassador status without prior
            notice for: reselling counterfeits or competing-brand inventory under JS branding, deceptive marketing or fake claims,
            fake-account abuse, harassment of other ambassadors or customers, doxxing, or violating these Terms. On termination for
            cause, your wallet balance is forfeited and the tester-kit fee is non-refundable.
          </p>
        </Section>

        <Section id="tester-kit" title="12. Tester Kit Terms">
          <p>
            The JS Fragrance Brand Ambassador Tester Kit is sold solely to verified ambassador applicants and includes the items listed
            on <a href="/tester-kit-checkout" className="text-[#D4AF37] hover:underline">/tester-kit-checkout</a> at the time of your purchase.
            By purchasing the tester kit you agree:
          </p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> The tester kit is for personal sampling and customer demonstration only — <strong className="text-white/80">strictly NOT for resale</strong> as standalone tester bottles.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Your tester-kit purchase is <strong className="text-white/80">non-refundable</strong> once payment has been approved by our team. See the <a href="/refund-policy" className="text-[#D4AF37] hover:underline">Refund Policy</a> for the full clause.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Newly released scents are added to the tester ecosystem at no additional cost where reasonably possible. Specific bottles included may evolve over time.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> If your ambassador status is terminated for cause, you forfeit any wallet balance and are not entitled to a refund of the tester-kit fee.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> The tester kit is shipped once per ambassador account and cannot be re-shipped after delivery has been confirmed.</li>
          </ul>
        </Section>

        <Section id="distributor" title="13. Distributor Program">
          <p>
            The Distributor Program is for serious resellers ordering in bulk. Distributors enjoy confidential tiered wholesale pricing
            and dedicated account support. Wholesale pricing is <strong className="text-white/80">never published publicly</strong> — it is
            visible only to verified, signed-in ambassadors and distributors via <a href="/wholesale" className="text-[#D4AF37] hover:underline">/wholesale</a>.
          </p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> <strong className="text-white/80">Tiered minimum order quantities (per single product):</strong> Distributor tier 100–299 pcs, Bulk Partner tier 300–499 pcs, Super Bulk tier 500+ pcs. Higher tiers unlock deeper per-unit margins.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> <strong className="text-white/80">Payment terms:</strong> full payment in advance before production starts. Production lead time depends on order size and is communicated before payment is accepted.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> <strong className="text-white/80">Shipping for bulk orders</strong> is paid by the distributor unless explicitly agreed otherwise (e.g., super-bulk shipping incentives).</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> <strong className="text-white/80">Returns</strong> for distributor orders are accepted only for manufacturing defects or shipping damage, reported within 3 days of delivery with photo evidence.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> <strong className="text-white/80">Private label / co-branding</strong> is available on bulk orders by separate written agreement.</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> JS may update tiered wholesale prices with reasonable notice; pending unfulfilled distributor orders honor the price agreed at the time of payment.</li>
          </ul>
        </Section>

        <Section id="prohibited" title="14. Prohibited Use">
          <p>You agree NOT to:</p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-red-400">✕</span> Resell our products as a different brand or strip our packaging</li>
            <li className="flex gap-2"><span className="text-red-400">✕</span> Resell tester-kit bottles individually as standalone products</li>
            <li className="flex gap-2"><span className="text-red-400">✕</span> Submit fake reviews, harass other customers, or impersonate JS Fragrance</li>
            <li className="flex gap-2"><span className="text-red-400">✕</span> Attempt to scrape, copy, or mirror the website or its content</li>
            <li className="flex gap-2"><span className="text-red-400">✕</span> Exploit promotions, referrals, or bundle pricing through abusive multi-account behaviour</li>
            <li className="flex gap-2"><span className="text-red-400">✕</span> Build downline / pyramid / matrix schemes around the JS brand — our program is one-level only</li>
            <li className="flex gap-2"><span className="text-red-400">✕</span> Use our images, content, or trademarks without written permission</li>
          </ul>
        </Section>

        <Section id="ip" title="15. Intellectual Property">
          <p>
            All website content — logos, product names, photography, copy, the JS Originals scent compositions, and source code —
            is the property of JS Fragrance and is protected under Philippine and international intellectual property law.
            Unauthorized reproduction or commercial use is prohibited.
          </p>
        </Section>

        <Section id="liability" title="16. Limitation of Liability">
          <p>
            JS Fragrance is not liable for indirect, incidental, or consequential damages arising from product use or website access.
            Our liability for any single product is limited to its purchase price.
          </p>
          <p>
            Customers with sensitive skin should patch-test before applying. Discontinue use if irritation occurs and consult a dermatologist.
          </p>
        </Section>

        <Section id="reviews" title="17. Customer Reviews & Submitted Content">
          <p>
            By submitting a review, photo, or feedback, you grant JS Fragrance a non-exclusive, royalty-free, worldwide license
            to display, edit, or reuse that content in marketing material, on product pages, and across our social channels.
            You may request removal at any time by contacting support.
          </p>
        </Section>

        <Section id="dispute" title="18. Disputes & Governing Law">
          <p>
            These terms are governed by the laws of the Republic of the Philippines. Disputes shall first be resolved via
            good-faith dialogue with our support team. Unresolved disputes shall be referred to the appropriate Philippine
            courts in Metro Manila.
          </p>
        </Section>

        <Section id="changes" title="19. Changes to These Terms">
          <p>
            We may update these terms periodically. Material changes will be announced on our homepage and via email to
            registered customers where applicable. Continued use after changes constitutes acceptance.
          </p>
        </Section>

        <div className="border-t border-white/10 pt-6">
          <p className="text-white/30 text-xs font-sans">
            Questions about these terms? Email <a href="mailto:support@jsfragrances.com" className="text-[#D4AF37] hover:underline">support@jsfragrances.com</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function TermsPage() {
  return <TermsHardcoded />;
}
